/**
 * What a gate is and what it decided (Feature 72): passing automatically,
 * empty, or so many conditions on the editor's canvas; passed, auto, blocked,
 * empty, or not reached on a run's. Shared by the canvas, the inspector, and
 * the record panel, so a gate reads the same everywhere.
 */
import type { LinkageGateOutcome, LinkageJoin, LinkageLink } from '@optum/api-client/types';

/**
 * What a gate is: locked open after a schedule, passing automatically, empty,
 * or conditions; a link out of a decision has none, only its branch (Feature 79).
 */
export type Gate =
  | { kind: 'auto' }
  | { kind: 'empty' }
  | { kind: 'conditions'; count: number }
  | { kind: 'branch'; name: string };

/** A link's gate, or the branch a link out of a gate node follows (Feature 79). */
export function gateOf(link: LinkageLink): Gate {
  if (link.branch !== undefined) return { kind: 'branch', name: link.branch };
  if (link.auto_pass) return { kind: 'auto' };
  const count = link.rules?.items.length ?? 0;
  if (count === 0) return { kind: 'empty' };
  return { kind: 'conditions', count };
}

/** The gate's words: "locked · auto", "auto", "empty", "2 conditions", or a branch's name. */
export function gateText(gate: Gate): string {
  if (gate.kind === 'branch') return gate.name;
  if (gate.kind === 'auto') return 'auto';
  if (gate.kind === 'empty') return 'empty';
  return `${gate.count} condition${gate.count === 1 ? '' : 's'}`;
}

/** What decides a gate, said on its hover card: how its conditions combine, or why none are read. */
export function gateSummary(gate: Gate, join: LinkageJoin): string {
  if (gate.kind === 'branch') return `the condition ${gate.name}: it runs when its gate takes it`;
  if (gate.kind === 'auto') return 'passes automatically';
  if (gate.kind === 'empty') return 'empty: it never passes, and a save refuses it';
  if (gate.count === 1) return 'it must hold';
  return join === 'all' ? 'all must hold' : 'any one must hold';
}

/** An outcome's words: "passed", or "not reached". */
export function outcomeText(outcome: LinkageGateOutcome): string {
  return outcome === 'not_reached' ? 'not reached' : outcome;
}

/** The step behind each empty gate, from the gates' `from→to` ids, without repeats. */
export function blockedSteps(emptyGates: readonly string[]): string[] {
  return [...new Set(emptyGates.map((id) => id.slice(id.indexOf('→') + 1)))];
}
